import { ovl, TemplateResult } from "../../../"
import {
  getDateValue,
  getDecimalValue,
  UIContextType,
} from "../../../global/globals"
import type {
  HookFieldGetEditHint_ReturnType,
  HookFieldGetEditHint_Type,
  HookFieldGetLabelRender_Type,
  HookFieldGetList_ReturnType,
  HookFieldLookupPostRefresh_Type,
  HookLookupRowRefreshed_Type,
} from "../../../global/hooks"
import {
  HookFieldGetEditHint,
  HookFieldGetLabelRender,
  HookFieldGetList,
  HookFieldLookupPostRefresh,
  HookLookupRowRefreshed,
} from "../../../global/hooks"

import type {
  DirectiveResult,
  SYSFeature,
  SYSForm,
  SYSState,
} from "../../../index"
import {
  _GetFieldList,
  CachedRendererData,
  GetRendererFn,
} from "../../table/helpers"

import type {
  ColumnDisplayDef,
  DisplayMode,
  EditDisplayMode,
  ListDefinition,
  LookupsType,
} from "../../table/table"
import type { SYSField, SYSFormState } from "../actions"

import { AppDefProcess } from "../../../config"
import { Data } from "../../../dataapi"
import {
  ProcessEnd,
  ProcessIsRunning,
  ProcessStart,
} from "../../longrunningprocesstracker/processapi"
import { ScreenCurrentState } from "../../screenapi"
import { FieldForceChange } from "../fieldapi"
import { FormGet } from "../formapi"
import { _setFieldFocus } from "../helper"
import { setVisibleValidation } from "../validators"
import type { ListState } from "./listcontrol"
import { SortGet } from "../../../global/globalapi"

export const _focusOutHelper = (e: Event, field: SYSField) => {
  // didnt work...251111 tried to fix but still didnt work as expected. thats why uncomment
  // //@ts-ignore
  // if (
  //   //@ts-ignore
  //   e.relatedTarget &&
  //   //@ts-ignore
  //   (e.relatedTarget.id.indexOf("SYSEditCancel") > -1 ||
  //     //@ts-ignore
  //     e.relatedTarget === "SYSNavigateBack")
  // ) {
  //   // e.preventDefault()
  //   // e.stopPropagation()
  //   console.log("set false")
  //   field._state.forceChange = false
  //   return
  // }
  _setFieldFocus(field, false)

  if (field._state.forceChange) {
    FieldForceChange(field)
  }
  setVisibleValidation(
    FormGet(<SYSForm>field.formType, { instance: field.formId }).formState,
    0
  )
}

export type LookupListPostData = {
  url: string
  filterValue: string
  uiContext: UIContextType
  paramList?: { [key: string]: {} }
  notFullList?: boolean
}

export const KeyValueListFromServerFn = async (
  state: SYSState,
  list: ListState,
  columnKey: string,
  lookupsState: LookupsType,
  filterValue: string,
  row: {},
  feature: SYSFeature,
  subFeature: string,
  formType: string,
  formId: any,
  //ignore timestamp, lastParam... and always issue a server req
  isInnerEvent?: boolean,
  forceNoSnack?: boolean,
  forceClear?: boolean,
  notFullList?: boolean
): Promise<ListDefinition> => {
  try {
    let url = state.ovl.apiUrl + list.serverEndpoint
    if (filterValue === undefined) {
      // important because the object gets stringified for param comparison and filterValue would be completely missung if undefined...
      filterValue = ""
    }
    isInnerEvent = isInnerEvent === true

    let screenCurrentState = ScreenCurrentState()
    let uiContext: UIContextType = {
      feature: <string>feature,
      tableDefId: undefined,
      formType,
      formId,
      screen: screenCurrentState?.screen,
      screenInstance: screenCurrentState?.instance,
    }
    let postData: LookupListPostData = {
      url,
      filterValue,
      uiContext,
      paramList: row,
      notFullList: notFullList === true,
    }
    if (!postData.paramList) {
      postData.paramList = {}
    }
    if (list.refreshMode === "ClearAndRefresh") {
      forceClear = true
    }
    let fieldId = list.valueField
    let listdata
    if (list.lookupDefKey) {
      listdata = lookupsState[list.lookupDefKey]
    } else {
      listdata = lookupsState[fieldId]
    }
    if (listdata === undefined) {
      throw new Error(
        `Ovl Error: Feature ${<string>(
          feature
        )}, column: ${columnKey}: listdata is empty. Did you forget to add lookupKey to the listdef?`
      )
    }

    let ignoreRefreshLogic = false
    let fn = ovl.getActions().app.features[feature]
    let functionName = HookFieldLookupPostRefresh.replace(
      "{subFeature}",
      subFeature
    ).replace("{fieldKey}", columnKey)
    if (fn?.hooks?.[functionName]) {
      let fnParam: HookFieldLookupPostRefresh_Type = {
        postToServerData: postData,
        showSnack: list.showRefreshFromServerSnack,
        cancelRefresh: isInnerEvent ? "Default" : "ForceRefresh",
        forceClear,
        innerEvent: isInnerEvent,
        info: {
          _row: row,
          _timeStamp: list._state.lastRefreshTimestamp,
          _lastPostToServerData: list._state.lastPostParams,
        },
      }
      await fn.hooks[functionName](fnParam)
      if (fnParam.cancelRefresh === "Cancel") {
        return listdata
      }

      if (fnParam.cancelRefresh === "ForceRefresh") {
        ignoreRefreshLogic = true
      }

      forceNoSnack = !fnParam.showSnack
    }

    let now = new Date().getTime()
    list._state.needsRefresh = false
    if (ignoreRefreshLogic) {
      list._state.needsRefresh = true
    } else {
      // refresh if either timestamp is old
      if (
        now - list._state.lastRefreshTimestamp >
        list.onlyRefreshFromServerIfOlderThanMs
      ) {
        list._state.needsRefresh = true
      }
      // or the params have changed
      if (!list._state.needsRefresh) {
        if (list._state.lastPostParams !== JSON.stringify(postData)) {
          list._state.needsRefresh = true
        }
      }
    }

    if (!list._state.needsRefresh) {
      return listdata
    }
    let options: AppDefProcess
    if (list.showRefreshFromServerSnack && !forceNoSnack) {
      options = { snack: { type: "BusyIndicator" } }
    }
    if (!ProcessIsRunning("SYSGetLookupData")) {
      ProcessStart("SYSGetLookupData", options)
    }

    let res = await Data(list.serverEndpoint, {
      filterValue: postData.filterValue,
      columnKey,
      uiContext: postData.uiContext,
      paramList: postData.paramList,
      notFullList: postData.notFullList,
    })
    // now sync the result with the local state
    if (res.data && res.data.data) {
      if (forceClear) {
        listdata.data = {}
      }
      list._state.lastRefreshTimestamp = now
      list._state.lastPostParams = JSON.stringify(postData)
      if (listdata.data === undefined) {
        listdata.data = {}
        list._state.lastRefreshTimestamp = 0
      }
      let localListState = listdata.data
      let serverData = res.data.data
      if (serverData && serverData.length > 0) {
        let ignoreNulls = listdata.nullHandling?.ignoreIfKeyIsNull
        // let fillWithEmpty =
        //   listdata.nullHandling?.replaceLookupColumnsWithEmptyIfNull
        let keyColumns = Object.keys(listdata.lookupDef).filter(
          (f) => listdata.lookupDef[f].isKey === true
        )
        let keyColumn
        if (keyColumns.length > 0) {
          keyColumn = keyColumns[0]
        }
        // check mandatory lookup columns, but only do it in the first row for now
        let mandatoryColumns = Object.keys(listdata.lookupDef).filter(
          (f) => !listdata.lookupDef[f].optional
        )

        let firstRow = serverData[0]
        mandatoryColumns.forEach((f) => {
          if (firstRow[f] === undefined) {
            console.error(
              `Ovl Lookup Sync: Column ${f} (Lookupdef ${list.lookupDefKey}) not found in results from server...`
            )
          }
        })

        serverData.forEach((k) => {
          if (k[fieldId] === null && ignoreNulls) {
            return
          }
          if (!localListState[k[fieldId]]) {
            localListState[k[fieldId]] = {}
          }
          Object.keys(k).forEach((c) => {
            let val = k[c]
            // if (c !== keyColumn && val === null && fillWithEmpty) {
            //   val = ""
            // }

            localListState[k[fieldId]][c] = val
          })
          if (fn?.hooks?.[HookLookupRowRefreshed]) {
            fn.hooks[HookLookupRowRefreshed](<HookLookupRowRefreshed_Type>{
              lookupDataRow: localListState[k[fieldId]],
              lookupDefKey: list.lookupDefKey,
            })
          }
        })
      }
    }
    return listdata
  } finally {
    ProcessEnd("SYSGetLookupData")
  }
}

export const FilterHitList = (
  list: ListState,
  filterValue: string,
  formState: SYSFormState,
  fieldId: string,
  top?: number,
  forceCacheBust?: boolean,
  type?: "inline" | "overlay"
): { filteredKeys: string[]; directValue?: any; directDescription?: any } => {
  let hitLength = {}
  //let functionName = FieldGetFilteredList.replace("%", fieldId)

  let dataList: HookFieldGetList_ReturnType = _GetFieldList(
    ovl.getActions().app.features[formState.feature].hooks[
      HookFieldGetList.replace("{subFeature}", formState.subFeature).replace(
        "{fieldKey}",
        fieldId
      )
    ],
    fieldId,
    formState,
    formState?.tableDefId,
    forceCacheBust
  )
  if (dataList.data) {
    let directValue
    let directDescription
    let res = Object.keys(dataList.data)
    let resForSelectIfEmpty = JSON.parse(JSON.stringify(res))
    let directKeyHits = res.filter((f) => f == filterValue)
    if (directKeyHits.length === 1) {
      directValue = directKeyHits[0]
      directDescription = dataList.data[directValue][list.displayField]
    }

    // // filter ignored
    let exact = false
    if (formState) {
      let field = formState.fields[fieldId]
      if (!field.list.acceptOnlyListValues) {
        exact = true
      }
      if (field.ui.displayEmptyIfValue !== undefined) {
        res = res.filter((f) => f !== field.ui.displayEmptyIfValue)
      }
    }

    let lookupTypes = dataList.lookupDef

    if (!lookupTypes) {
      lookupTypes = {}
      lookupTypes[list.displayField] = { type: "text" }
      lookupTypes[list.valueField] = { type: "text" }
    }
    if (!filterValue) {
      filterValue = ""
    }

    res = res.filter((f) => {
      if (filterValue === "") {
        return true
      }
      let checkRow = dataList.data[f]
      let found = false
      Object.keys(lookupTypes)
        .filter(
          (f) =>
            lookupTypes[f].ignoreForSearch !== true &&
            lookupTypes[f].type !== "html"
        )
        .forEach((c) => {
          let checkVal = checkRow[c]
          if (checkVal !== undefined) {
            let typ = lookupTypes[c].type
            if (typ === "date") {
              checkVal = getDateValue(checkVal)
            } else if (typ === "decimal") {
              checkVal = getDecimalValue(checkVal)
            }
            if (checkVal === null) {
              checkVal = ""
            }
            checkVal = checkVal.toString()
            if (!exact) {
              checkVal = checkVal.toLowerCase()
              filterValue = filterValue.toLowerCase()
            }
            let idx = checkVal.indexOf(filterValue)
            if (idx > -1) {
              //let length = checkVal.length - filterValue.length
              if (!hitLength[f] || idx < hitLength[f]) {
                hitLength[f] = idx
              }
              found = true
            }
          }
        })
      return found
    })
    if (dataList.sortMode === undefined) {
      dataList.sortMode = "HitLengthThenCustom"
    }
    let sortCol
    let sortCols = Object.keys(lookupTypes).filter(
      (f) => lookupTypes[f].isSortCol === true
    )
    if (sortCols.length > 0) {
      sortCol = sortCols[0]
    }

    res = res.sort((a, b) => {
      let sres = 0
      if (dataList.sortMode === "HitLengthThenCustom") {
        sres = filterValue ? hitLength[a] - hitLength[b] : 0
        if (sres === 0 && dataList.sortFn !== undefined) {
          sres = dataList.sortFn(dataList.data[a], dataList.data[b])
        } else if (sres === 0 && sortCol) {
          sres = SortGet(dataList.data[a][sortCol], dataList.data[b][sortCol])
        }
      } else {
        if (dataList.sortFn !== undefined) {
          sres = dataList.sortFn(dataList.data[a], dataList.data[b])
        } else if (sortCol) {
          sres = SortGet(dataList.data[a][sortCol], dataList.data[b][sortCol])
        }
        if (sres === 0) {
          sres = filterValue ? hitLength[a] - hitLength[b] : 0
        }
      }
      return sres
    })
    if (top) {
      res.splice(top)
    }

    if (!directValue && res.length === 1) {
      let refVal = dataList.data[res[0]][list.valueField]
      if (refVal) {
        directValue = refVal
      } else {
        directValue = res[0]
      }
      directDescription = dataList.data[res[0]][list.displayField]
    }
    if (res.length === 0 && list.mode === "Select" && type === "overlay") {
      res = resForSelectIfEmpty
    }
    return { filteredKeys: res, directValue, directDescription }
  }
  return { filteredKeys: [] }
}

export const _GetListValue = (
  list: ListState,
  value: string,
  listdata: ListDefinition,
  lookupColumnKey?: string,
  useDisplayValue?: boolean
) => {
  let displayField = lookupColumnKey
    ? lookupColumnKey
    : useDisplayValue
      ? list.displayField
      : list.valueField
  // was let listValue = value before. Ruuning into issues when eg. usecase BPFilter BAR FieldVal("CardCode", {lookupColumn:"CardType"}) was returning CardCode
  let listValue = displayField !== list.valueField ? null : value
  if (listdata.data) {
    if (listdata.data[value]) {
      listValue = listdata.data[value][displayField]
    } else if (!list.acceptOnlyListValues) {
      listValue = value
    }
  }
  return listValue
}

export const GetRowFromFormState = (
  formState: SYSFormState,
  explicitlySetKey: string = undefined,
  explicitlySetValue: any = undefined
) => {
  //pauseTracking()
  //try {
  let fields = formState.fields
  let row = formState.row

  let fieldKeys = []
  let rowKeys = []
  if (row) {
    rowKeys = Object.keys(row)
  }
  fieldKeys = Object.keys(fields)

  let allKeys = [...new Set([...rowKeys, ...fieldKeys])]

  let res = allKeys.reduce((val, k) => {
    if (fields[k]) {
      val[k] = fields[k].convertedValue
    } else {
      if (row) {
        val[k] = row[k]
      }
    }
    return val
  }, {})

  if (explicitlySetKey) {
    res[explicitlySetKey] = explicitlySetValue
  }
  return res
  // } finally {
  //   //resumeTracking()
  // }
}

export let cachedValueRendererFn: Map<string, CachedRendererData> = new Map<
  string,
  CachedRendererData
>()

export let cachedRendererFn: Map<string, CachedRendererData> = new Map<
  string,
  CachedRendererData
>()

export const GetValueFromCustomFunction = (
  row: { [key: string]: {} },
  field: SYSField,
  formState: SYSFormState,
  align: string,

  state: SYSState
): TemplateResult | TemplateResult[] | string => {
  let rendererFn = GetRendererFn(
    formState.feature,
    cachedValueRendererFn,
    HookFieldGetEditHint.replace("{subFeature}", formState.subFeature),
    field.fieldKey
  )
  if (rendererFn) {
    let val: HookFieldGetEditHint_ReturnType = rendererFn(<
      HookFieldGetEditHint_Type
    >{
      row,
      align,
      field,
      formState,
      displayMode: formState.isInline
        ? <EditDisplayMode>"EditInline"
        : <EditDisplayMode>"Edit",
    })
    if (val !== undefined) {
      return val
    }
  }
  return null
}

// const fillReactiveRows = (
//   originalRow: { [key: string]: {} },
//   formState: SYSFormState
// ): { [key: string]: {} } => {
//   let row = {}
//   if (originalRow !== undefined) {
//     row = JSON.parse(JSON.stringify(originalRow), stringifyReplacer)
//   }
//   Object.keys(formState.fields).forEach((f) => {
//     let field = formState.fields[f]
//     row[field.fieldKey] = field.convertedValue
//   })
//   return row
// }

export const getColumnDefsFromFormState = (
  formState: SYSFormState
): { [key: string]: ColumnDisplayDef } => {
  let colDisplayDefs = Object.keys(formState.fields).reduce((val, f) => {
    let field = formState.fields[f]
    let res: ColumnDisplayDef = {
      list: field.list,
      ui: field.ui,
      type: field.type,
    }
    val[field.fieldKey] = res
    return val
  }, {})
  return colDisplayDefs
}

export const GetFieldLabel = (
  field: SYSField,
  feature: SYSFeature,
  caption: string | DirectiveResult
): any => {
  let formState = ovl.getState().ovl.forms[field.formType][field.formId]
  if (!formState) {
    return
  }
  let state = ovl.getState()
  let rendererFn = GetRendererFn(
    feature,
    cachedRendererFn,
    HookFieldGetLabelRender.replace("{subFeature}", formState.subFeature),
    field.fieldKey
  )

  if (rendererFn) {
    let labelRender = rendererFn(<HookFieldGetLabelRender_Type>{
      columnKey: field.fieldKey,
      caption,
      align: field.ui.align,
      formState,
      displayMode: <DisplayMode>"Edit",
      state,
    })

    if (labelRender !== undefined) {
      if (labelRender.res !== undefined) {
        caption = labelRender.res
      }
      if (labelRender.valueRaw !== undefined) {
        field.ui.labelCaption = labelRender.valueRaw
      }
    }
  }

  return caption
}
